import { redirect } from 'react-router';
import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { flashMessage } from '@gonfalon/flash-messages/flashMessage';

import { readProjectContextFromRequest } from '../internal/readProjectContextFromRequest';
import { projectContextSearchParamsUpdater } from '../projectContextSearchParamsUpdater';

import {
  fetchProjectContextForRequest,
  NO_AVAILABLE_ENVIRONMENT_ERROR,
  type ProjectContextForRequest,
} from './internal/fetchProjectContextForRequest';

export class ContextError extends Error {
  name = 'ContextError';
  message: string;
  stack?: string;

  constructor({ message, cause, stack }: { message: string; cause?: unknown; stack?: string }) {
    super(message);
    this.message = message;
    this.cause = cause;
    if (stack) {
      this.stack = stack || undefined;
    }
  }

  toJSON() {
    return { message: this.message, stack: this.stack };
  }
}

/**
 * Ensures that a valid project context is present in the request.
 * If no project exists in the url, it redirects to the root URL.
 * If the project context is not found, it redirects to the project settings page with an appropriate message.
 * If the project context requires a redirect, it updates the URL and redirects accordingly.
 * A project context may require a redirect if the requested environment is not found in the project, or no environments are requested.
 *
 * @param {Request} request - The incoming request object.
 * @returns {Promise<ProjectContextForRequest>} - The project context for the request.
 * @throws {Error} - Throws an error if there is an unexpected issue fetching the project context data.
 */
export async function requireProjectContext(request: Request) {
  const projectContext = readProjectContextFromRequest(request);

  // If we don't have a project context in the URL, there's not much we can do
  if (!projectContext) {
    const url = new URL('/', window.location.origin);
    url.searchParams.set('root-redirect', 'true');
    throw redirect(url.toString());
  }

  let projectContextForRequest: ProjectContextForRequest;
  try {
    projectContextForRequest = await fetchProjectContextForRequest(projectContext, request.signal);
  } catch (error) {
    if (isRESTAPIError(error)) {
      switch (error.status) {
        case 404:
          flashMessage(
            'missing-project',
            `Project with key "${projectContext.projectKey}" not found. Select another project to continue.`,
          );
          throw redirect('/settings/projects');
        case 403:
          flashMessage(
            'unauthorized-project',
            `No access to project with key "${projectContext.projectKey}". Check with your admin, or select another project to continue.`,
          );
          throw redirect('/settings/projects');
        default:
          break;
      }
    }

    if (error instanceof Error) {
      if (error.message === NO_AVAILABLE_ENVIRONMENT_ERROR) {
        throw redirect('/settings/projects');
      }
      throw new ContextError({
        message: `Unexpected error fetching project context data: ${error.message}`,
        cause: error,
        stack: 'componentStack' in error && typeof error.componentStack === 'string' ? error.componentStack : undefined,
      });
    } else {
      throw new ContextError({
        message: `Unknown error fetching project context data, cause: ${JSON.stringify(error)}`,
        cause: error,
      });
    }
  }

  const { project, environments, context } = projectContextForRequest;

  let shouldReloadContext = false;

  // if the selected environment has changed, we need to reload the context
  if (context.selectedEnvironmentKey !== projectContext.selectedEnvironmentKey) {
    shouldReloadContext = true;
  }

  // if an environment within the context is not found in the project, remove it from the context and reload the context
  for (const key of context.environmentKeys) {
    if (!environments.some((env) => env.key === key)) {
      context.environmentKeys.delete(key);
      shouldReloadContext = true;
    }
  }

  // If the selected environment is not found in the project, set it to the first remaining existing environment, if any, and reload the context
  if (!environments.some((env) => env.key === context.selectedEnvironmentKey)) {
    // default the selected environment key to the empty string in case there are no environments left
    context.selectedEnvironmentKey = context.environmentKeys.values().next().value ?? '';
    shouldReloadContext = true;
  }

  // if we need to reload the context, redirect to the new URL
  if (shouldReloadContext) {
    const nextUrl = new URL(request.url);
    const newSearchParamsInit = projectContextSearchParamsUpdater(nextUrl.searchParams, {
      ...context,
      selectedViewKey: context.selectedViewKey ?? undefined,
    });
    nextUrl.search = newSearchParamsInit.toString();
    throw redirect(nextUrl.toString());
  }

  return {
    project,
    environments,
    context,
  };
}
